import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import PushSubscribeButton from "@/components/PushSubscribeButton";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ClipboardList,
  Factory,
  CheckCircle2,
  AlertTriangle,
  Users,
  Package,
  Scissors,
  Loader2,
  Send,
  ArrowRight,
  FileClock,
  Boxes,
  GitBranch,
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from "recharts";

const KPI_META = [
  { key: "active_orders", label: "Aktif Sipariş", icon: ClipboardList, color: "text-blue-500", accent: "bg-blue-600", link: "/satislar" },
  { key: "in_production", label: "Üretimde", icon: Factory, color: "text-blue-400", accent: "bg-blue-600", link: "/satislar?filter=in_production" },
  { key: "completed_orders", label: "Tamamlanan", icon: CheckCircle2, color: "text-emerald-400", accent: "bg-emerald-600", link: "/satislar?stage=tamamlandi" },
  { key: "delayed_orders", label: "Gecikmiş", icon: AlertTriangle, color: "text-red-400", accent: "bg-red-600", link: "/satislar?filter=delayed" },
  { key: "total_customers", label: "Müşteri", icon: Users, color: "text-slate-300", accent: "bg-slate-700", link: "/musteri" },
  { key: "fabric_alerts", label: "Kumaş Uyarısı", icon: Scissors, color: "text-yellow-400", accent: "bg-yellow-600", link: "/stok" },
];

// P1 — Ayrı satırda göster (KPI Grid'in altında yeni satır) —
// mevcut KPI kartlarını bozmadan ekleniyor.
const MINI_META = [
  {
    key: "pending_quotes",
    label: "Bekleyen Teklifler",
    subKey: "pending_quotes_value",
    subFmt: (v) => `₺${Number(v || 0).toLocaleString("tr-TR", { maximumFractionDigits: 0 })}`,
    subLabel: "Toplam Değer",
    icon: FileClock,
    accent: "bg-amber-600",
    link: "/teklifler",
  },
  {
    key: "sample_at_customer",
    label: "Müşteride Numune",
    subKey: "sample_overdue",
    subLabel: "Gecikmiş",
    subTone: "rose",
    icon: Boxes,
    accent: "bg-cyan-600",
    link: "/numuneler",
    trendKeys: ["sample_dropped_this_month", "sample_returned_this_month"],
    trendLabels: ["Bu Ay Bırakılan", "Bu Ay Dönen"],
  },
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [partialConv, setPartialConv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingAlert, setSendingAlert] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/summary");
        setData(data);
      } catch (err) {
        // Silently swallow — if session expired, AuthContext will redirect to login.
        // Other errors just leave the dashboard in loading-empty state.
      } finally {
        setLoading(false);
      }
    })();
    // P3: Numune kısmi satış dönüşüm mini kartı — samples.view yetkisi
    // yoksa 403 döner, sessizce göz ardı edilir.
    (async () => {
      try {
        const { data } = await api.get("/samples/kpis/partial-conversions?days=90&limit=5");
        setPartialConv(data);
      } catch { /* silent — no perm or empty */ }
    })();
  }, []);

  const sendDelayedAlert = async () => {
    setSendingAlert(true);
    try {
      const { data } = await api.post("/push/notify-delayed");
      toast.success(`Uyarı gönderildi: ${data.body} (${data.sent}/${data.total})`);
    } catch (err) {
      const msg = formatApiError ? formatApiError(err) : (err?.response?.data?.detail || err?.message);
      toast.error(typeof msg === "string" ? msg : "Gönderim başarısız");
    } finally {
      setSendingAlert(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Kontrol Paneli"
        subtitle="Üretim akışı gerçek zamanlı özet"
        breadcrumbs={["Ana Sayfa", "Kontrol Paneli"]}
        actions={
          <div className="flex items-center gap-2">
            <PushSubscribeButton />
            <Button
              data-testid="send-delayed-alert-btn"
              onClick={sendDelayedAlert}
              disabled={sendingAlert}
              size="sm"
              variant="outline"
              className="border-blue-300 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/30 text-blue-800 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-950/60 hover:text-blue-900 dark:hover:text-blue-200 disabled:opacity-60 rounded-sm text-xs h-8"
            >
              {sendingAlert ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1.5" /> Gecikme Uyarısı</>}
            </Button>
          </div>
        }
      />

      <div className="p-6 lg:p-8 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
          </div>
        ) : (
          <>
            {/* KPI Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {KPI_META.map((k) => {
                const Icon = k.icon;
                const value = data?.[k.key] ?? 0;
                return (
                  <Card
                    key={k.key}
                    data-testid={`kpi-${k.key}`}
                    role={k.link ? "button" : undefined}
                    tabIndex={k.link ? 0 : undefined}
                    onClick={k.link ? () => navigate(k.link) : undefined}
                    onKeyDown={k.link ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(k.link); } } : undefined}
                    className={`bg-slate-900 border-slate-800 rounded-sm p-5 transition-all ${
                      k.link
                        ? "cursor-pointer hover:border-blue-600/70 hover:bg-slate-800/60 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        : "hover:border-slate-600"
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className={`w-9 h-9 ${k.accent} flex items-center justify-center rounded-sm`}>
                        <Icon className="w-4 h-4 text-white" strokeWidth={2.5} />
                      </div>
                      {k.link && (
                        <ArrowRight className="w-3.5 h-3.5 text-slate-600 group-hover:text-blue-500 transition-colors" />
                      )}
                    </div>
                    <div className="text-3xl font-bold text-slate-50 tracking-tight font-mono">
                      {value}
                    </div>
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-mono mt-1">
                      {k.label}
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* P1 — Mini Cards row (Bekleyen Teklifler + Numune Trend) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {MINI_META.map((m) => {
                const Icon = m.icon;
                const value = data?.[m.key] ?? 0;
                const subValue = m.subKey ? data?.[m.subKey] ?? 0 : null;
                const subFmt = m.subFmt || ((v) => v);
                const subToneCls = m.subTone === "rose"
                  ? (Number(subValue) > 0 ? "text-rose-400" : "text-slate-500")
                  : "text-slate-300";
                return (
                  <Card
                    key={m.key}
                    data-testid={`mini-${m.key}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(m.link)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(m.link); } }}
                    className="bg-slate-900 border-slate-800 rounded-sm p-5 cursor-pointer hover:border-blue-600/70 hover:bg-slate-800/60 hover:-translate-y-0.5 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className={`w-9 h-9 ${m.accent} flex items-center justify-center rounded-sm`}>
                        <Icon className="w-4 h-4 text-white" strokeWidth={2.5} />
                      </div>
                      <ArrowRight className="w-3.5 h-3.5 text-slate-600" />
                    </div>
                    <div className="flex items-baseline gap-3">
                      <div className="text-3xl font-bold text-slate-50 tracking-tight font-mono">{value}</div>
                      {m.subKey && (
                        <div className="text-xs text-slate-500 font-mono uppercase tracking-wider">
                          <span className={`font-semibold ${subToneCls}`}>{subFmt(subValue)}</span>
                          <span className="ml-1">· {m.subLabel}</span>
                        </div>
                      )}
                    </div>
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-mono mt-1">{m.label}</div>
                    {m.trendKeys && (
                      <div className="mt-3 pt-3 border-t border-slate-800 flex items-center gap-4 text-[10px] text-slate-500 font-mono uppercase tracking-wider">
                        {m.trendKeys.map((tk, i) => (
                          <div key={tk} data-testid={`mini-${m.key}-trend-${tk}`}>
                            <span className="text-slate-200 font-semibold">{data?.[tk] ?? 0}</span>
                            <span className="ml-1">· {m.trendLabels[i]}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>

            {/* P3: En Çok Kısmi Satışa Dönüşen Numuneler (son 90 gün, top 5) */}
            {partialConv && (partialConv.rows || []).length > 0 && (
              <Card className="bg-slate-900 border-slate-800 rounded-sm p-6" data-testid="partial-conversions-card">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-bold text-slate-50 tracking-tight flex items-center gap-2">
                      <GitBranch className="w-4 h-4 text-cyan-400" />
                      En Çok Kısmi Satışa Dönüşen Numuneler
                    </h2>
                    <p className="text-xs text-slate-500 font-mono uppercase tracking-widest mt-1">
                      Son {partialConv.window_days || 90} Gün · Top {(partialConv.rows || []).length} · Süreç Sağlık Göstergesi
                    </p>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-slate-500 uppercase tracking-wider text-[10px] border-b border-slate-800">
                        <th className="text-left px-3 py-2 font-normal">Müşteri</th>
                        <th className="text-left px-3 py-2 font-normal">Ürün</th>
                        <th className="text-center px-3 py-2 font-normal">Verilen</th>
                        <th className="text-center px-3 py-2 font-normal">Satışa Dönen</th>
                        <th className="text-center px-3 py-2 font-normal">Müşteride Kalan</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/60">
                      {(partialConv.rows || []).map((r, i) => (
                        <tr key={`${r.sample_id}-${i}`} data-testid={`partial-conv-row-${i}`}
                          className="text-slate-300 hover:bg-slate-800/30 transition cursor-pointer"
                          onClick={() => navigate(`/numuneler?sample_id=${r.sample_id}`)}>
                          <td className="px-3 py-2 font-semibold text-slate-100">{r.customer_name}</td>
                          <td className="px-3 py-2 text-slate-300">{r.product_label}</td>
                          <td className="px-3 py-2 text-center font-mono">{r.given_qty}</td>
                          <td className="px-3 py-2 text-center font-mono text-cyan-400 font-bold">{r.converted_qty}</td>
                          <td className="px-3 py-2 text-center font-mono">
                            <span className={r.remaining_at_customer > 0 ? "text-sky-400" : "text-slate-500"}>
                              {r.remaining_at_customer}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right"><ArrowRight className="w-3.5 h-3.5 text-slate-600 inline" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Stage Distribution Chart */}
            <Card className="bg-slate-900 border-slate-800 rounded-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-bold text-slate-50 tracking-tight">
                    Üretim Aşama Dağılımı
                  </h2>
                  <p className="text-xs text-slate-500 font-mono uppercase tracking-widest mt-1">
                    20 Aşamalı Akış · Aktif Sipariş Sayısı
                  </p>
                </div>
                <Package className="w-5 h-5 text-slate-600" />
              </div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data?.stage_distribution || []} margin={{ left: -20, right: 8, top: 8, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                    <XAxis
                      dataKey="label"
                      stroke="#64748b"
                      tick={{ fontSize: 10, fill: "#94a3b8" }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval={0}
                    />
                    <YAxis stroke="#64748b" tick={{ fontSize: 11, fill: "#94a3b8" }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{
                        background: "#0f172a",
                        border: "1px solid #334155",
                        borderRadius: 2,
                        fontSize: 12,
                      }}
                      cursor={{ fill: "rgba(234, 88, 12, 0.08)" }}
                    />
                    <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                      {(data?.stage_distribution || []).map((entry, i) => (
                        <Cell key={i} fill={entry.stage === "tamamlandi" ? "#10b981" : "#2563eb"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
